import {useCallback} from 'react';
import {view} from '@sail/ui';
import {defineMessages, useIntl} from 'react-intl';
import type {RatingValue} from './types';
import {colors, transitions} from './theme';

const messages = defineMessages({
  terrible: {
    id: 'support_conversations_client.csat.dotcom.rating.terrible',
    description: 'Label for dotcom CSAT rating 1 out of 5',
    defaultMessage: 'Terrible',
  },
  bad: {
    id: 'support_conversations_client.csat.dotcom.rating.bad',
    description: 'Label for dotcom CSAT rating 2 out of 5',
    defaultMessage: 'Bad',
  },
  okay: {
    id: 'support_conversations_client.csat.dotcom.rating.okay',
    description: 'Label for dotcom CSAT rating 3 out of 5',
    defaultMessage: 'Okay',
  },
  good: {
    id: 'support_conversations_client.csat.dotcom.rating.good',
    description: 'Label for dotcom CSAT rating 4 out of 5',
    defaultMessage: 'Good',
  },
  great: {
    id: 'support_conversations_client.csat.dotcom.rating.great',
    description: 'Label for dotcom CSAT rating 5 out of 5',
    defaultMessage: 'Great',
  },
  rateButtonLabel: {
    id: 'support_conversations_client.csat.dotcom.rating.ariaLabel',
    description: 'Accessible label for rating buttons',
    defaultMessage: 'Rate {sentiment} ({rating} out of 5)',
  },
});

type Props = {
  rating: RatingValue;
  selected: boolean;
  disabled?: boolean;
  dataTestId?: string;
  onClick: () => void;
  accentColor?: string;
};

export const DotComRatingButton = ({
  rating,
  selected,
  disabled = false,
  dataTestId,
  onClick,
  accentColor = colors.brand[500],
}: Props) => {
  const intl = useIntl();

  const getRatingLabel = (ratingScore: RatingValue): string => {
    switch (ratingScore) {
      case '1':
        return intl.formatMessage(messages.terrible);
      case '2':
        return intl.formatMessage(messages.bad);
      case '3':
        return intl.formatMessage(messages.okay);
      case '4':
        return intl.formatMessage(messages.good);
      case '5':
        return intl.formatMessage(messages.great);
      default:
        return '';
    }
  };

  const handleClick = useCallback(() => {
    if (!disabled) {
      onClick();
    }
  }, [disabled, onClick]);

  const sentiment = getRatingLabel(rating);
  const ariaLabel = intl.formatMessage(messages.rateButtonLabel, {
    sentiment,
    rating,
  });

  return (
    <view.button
      type="button"
      data-testid={dataTestId}
      disabled={disabled}
      onClick={handleClick}
      aria-pressed={selected}
      aria-label={ariaLabel}
      css={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '60px',
        height: '38px',
        borderRadius: 'medium',
        border: '1px solid',
        borderColor: selected ? accentColor : colors.neutral[50],
        background: selected ? accentColor : colors.neutral[0],
        color: selected ? colors.neutral[0] : colors.neutral[800],
        font: 'label.medium',
        fontWeight: selected ? 'semibold' : 'normal',
        transition: transitions.default,
        transitionProperty: 'background, color, border-color',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.7 : 1,
        ':hover': {
          borderColor: disabled ? undefined : colors.neutral[200],
        },
      }}
    >
      {rating}
    </view.button>
  );
};
